/**
 * Profile page with avatar upload and display name editing
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { userService } from '@/services/user.service';
import { AvatarUpload } from '@/components/profile/AvatarUpload';
import { StoryGrid } from '@/components/stories/StoryGrid';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { queryKeys } from '@/lib/query-client';
import { useUserStories } from '@/services/stories';
import { useNavigate, useParams } from 'react-router-dom';
import { ThemeToggle } from '@/components/theme/ThemeToggle';

// Zod validation schema
const profileSchema = z.object({
  displayName: z
    .string()
    .min(1, 'Display name is required')
    .max(50, 'Display name must be 50 characters or less')
    .regex(
      /^[a-zA-Z0-9_\s]+$/,
      'Display name can only contain letters, numbers, spaces, and underscores'
    ),
  bio: z.string().max(200, 'Bio must be 200 characters or less').optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

export function ProfilePage() {
  const { user, idToken } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();

  // Determine if viewing own profile or another user's profile
  const isOwnProfile = !userId || userId === user?.userId;
  const profileUserId = userId || user?.userId || '';

  // Fetch user's stories
  const {
    data: storiesData,
    isLoading: storiesLoading,
    isError: storiesError,
    error: storiesErrorObj,
  } = useUserStories(profileUserId);

  const [formData, setFormData] = useState<ProfileFormData>({
    displayName: user?.displayName || '',
    bio: user?.bio || '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof ProfileFormData, string>>>({});
  const [serverError, setServerError] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Mutation for updating profile
  const updateProfileMutation = useMutation({
    mutationFn: async (data: ProfileFormData) => {
      if (!idToken) throw new Error('Not authenticated');
      return await userService.updateMe(idToken, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    },
    onError: (error) => {
      setServerError(error instanceof Error ? error.message : 'Failed to update profile');
    },
  });

  const handleChange =
    (field: keyof ProfileFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      // Clear field error on change
      if (errors[field]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
      // Clear server error and success on change
      if (serverError) {
        setServerError('');
      }
      if (showSuccess) {
        setShowSuccess(false);
      }
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});
    setServerError('');
    setShowSuccess(false);

    // Validate form data
    const result = profileSchema.safeParse(formData);

    if (!result.success) {
      const fieldErrors: Partial<Record<keyof ProfileFormData, string>> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof ProfileFormData] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    // Submit to backend
    updateProfileMutation.mutate(formData);
  };

  if (!user) {
    return (
      <div className="container mx-auto max-w-2xl p-6">
        <Alert>
          <AlertDescription>Loading profile...</AlertDescription>
        </Alert>
      </div>
    );
  }

  const isModified = formData.displayName !== user.displayName || formData.bio !== (user.bio || '');

  // If viewing another user's profile, show simplified view
  if (!isOwnProfile) {
    // Get user info from stories data (first story's user object)
    const profileUser = storiesData && storiesData.length > 0 ? storiesData[0].user : null;

    return (
      <div className="container mx-auto max-w-2xl p-6">
        <Card>
          <CardHeader>
            <CardTitle>{profileUser?.displayName || 'User Profile'}</CardTitle>
            <CardDescription>View stories from this user</CardDescription>
          </CardHeader>
          <CardContent>
            {profileUser && (
              <div className="mb-6 flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted text-2xl font-semibold">
                  {profileUser.displayName
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)}
                </div>
                <div>
                  <h2 className="text-lg font-semibold">{profileUser.displayName}</h2>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* User's Stories Section */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Stories</CardTitle>
            <CardDescription>
              {storiesData?.length
                ? `${storiesData.length} stor${storiesData.length === 1 ? 'y' : 'ies'}`
                : 'No stories uploaded yet'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <StoryGrid
              stories={storiesData || []}
              isLoading={storiesLoading}
              isError={storiesError}
              error={storiesErrorObj}
              onStoryClick={(storyId) => navigate(`/story/${storyId}`)}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Own profile - show full edit form
  return (
    <div className="container mx-auto max-w-2xl p-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <CardTitle>Profile Settings</CardTitle>
              <CardDescription>Manage your profile information and avatar</CardDescription>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="shrink-0"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar Upload */}
          <div>
            <Label className="mb-4 block text-base">Profile Picture</Label>
            <AvatarUpload
              currentAvatarUrl={user.avatarUrl}
              displayName={user.displayName}
              onUploadComplete={() => {
                // Avatar upload automatically invalidates user query
              }}
            />
          </div>

          {/* Profile Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {showSuccess && (
              <Alert>
                <AlertDescription>Profile updated successfully!</AlertDescription>
              </Alert>
            )}

            {serverError && (
              <Alert variant="destructive">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            )}

            {/* Email (read-only) */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={user.email} disabled className="bg-muted" />
              <p className="text-xs text-muted-foreground">Email cannot be changed</p>
            </div>

            {/* Display Name */}
            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name</Label>
              <Input
                id="displayName"
                type="text"
                placeholder="Your name"
                value={formData.displayName}
                onChange={handleChange('displayName')}
                disabled={updateProfileMutation.isPending}
                aria-invalid={!!errors.displayName}
                aria-describedby={errors.displayName ? 'displayName-error' : undefined}
              />
              {errors.displayName && (
                <p id="displayName-error" className="text-sm text-destructive">
                  {errors.displayName}
                </p>
              )}
            </div>

            {/* Bio */}
            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                placeholder="Tell us about yourself..."
                value={formData.bio}
                onChange={handleChange('bio')}
                disabled={updateProfileMutation.isPending}
                rows={4}
                maxLength={200}
                aria-invalid={!!errors.bio}
                aria-describedby={errors.bio ? 'bio-error' : undefined}
              />
              {errors.bio && (
                <p id="bio-error" className="text-sm text-destructive">
                  {errors.bio}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {formData.bio?.length || 0} / 200 characters
              </p>
            </div>

            {/* Appearance Settings */}
            <div className="border-t pt-4">
              <h3 className="mb-4 text-sm font-medium">Appearance</h3>
              <ThemeToggle />
            </div>

            {/* Account Info */}
            <div className="border-t pt-4">
              <h3 className="mb-2 text-sm font-medium">Account Information</h3>
              <div className="space-y-1 text-sm text-muted-foreground">
                <p>User ID: {user.userId}</p>
                <p>Joined: {new Date(user.createdAt).toLocaleDateString()}</p>
                {user.updatedAt !== user.createdAt && (
                  <p>Last updated: {new Date(user.updatedAt).toLocaleDateString()}</p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={!isModified || updateProfileMutation.isPending}
              className="w-full"
            >
              {updateProfileMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving changes...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save changes
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* User's Stories Section */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>My Stories</CardTitle>
          <CardDescription>
            {storiesData?.length
              ? `${storiesData.length} stor${storiesData.length === 1 ? 'y' : 'ies'}`
              : 'No stories uploaded yet'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <StoryGrid
            stories={storiesData || []}
            isLoading={storiesLoading}
            isError={storiesError}
            error={storiesErrorObj}
            onStoryClick={(storyId) => navigate(`/story/${storyId}`)}
          />
        </CardContent>
      </Card>
    </div>
  );
}
